import { useState } from 'react';
import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { DateRange } from '@/types/adminActivity';

const PRESETS = [
  { label: 'Past 7 days', days: 7 },
  { label: 'Past 14 days', days: 14 },
  { label: 'Past 30 days', days: 30 },
  { label: 'Past 90 days', days: 90 },
] as const;

interface DateRangeFilterProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

export function DateRangeFilter({ value, onChange }: DateRangeFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [localRange, setLocalRange] = useState<{ from?: Date; to?: Date }>(() => {
    const from = new Date(value.start);
    from.setHours(0, 0, 0, 0);
    const to = new Date(value.end);
    to.setHours(0, 0, 0, 0);
    return { from, to };
  });

  function handleOpenChange(open: boolean): void {
    if (open) {
      // Normalize to start-of-day so react-day-picker doesn't inherit end-of-day timestamps
      const from = new Date(value.start);
      from.setHours(0, 0, 0, 0);
      const to = new Date(value.end);
      to.setHours(0, 0, 0, 0);
      setLocalRange({ from, to });
    }
    setIsOpen(open);
  }

  function handlePreset(days: number): void {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date();
    start.setDate(start.getDate() - (days - 1));
    start.setHours(0, 0, 0, 0);
    onChange({ start, end });
    setIsOpen(false);
  }

  function handleCustomRange(range: { from?: Date; to?: Date } | undefined): void {
    if (!range?.from) return;

    // If we had a complete range, any click starts a fresh two-click selection
    if (localRange.from && localRange.to) {
      const fromUnchanged = range.from.getTime() === localRange.from.getTime();
      const clickedDate = fromUnchanged && range.to ? range.to : range.from;
      setLocalRange({ from: clickedDate, to: undefined });
      return;
    }

    // Partial range → update local state (and commit when both dates are picked)
    setLocalRange(range);

    if (range.from && range.to) {
      const endOfDay = new Date(range.to);
      endOfDay.setHours(23, 59, 59, 999);
      onChange({ start: range.from, end: endOfDay });
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'justify-start text-left font-normal',
            !value && 'text-muted-foreground'
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {format(value.start, 'MMM d, yyyy')} - {format(value.end, 'MMM d, yyyy')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="flex">
          <div className="flex flex-col gap-1 border-r p-3">
            <p className="text-sm font-medium mb-2">Presets</p>
            {PRESETS.map(({ label, days }) => (
              <Button
                key={days}
                variant="ghost"
                size="sm"
                className="justify-start"
                onClick={() => handlePreset(days)}
              >
                {label}
              </Button>
            ))}
          </div>

          <div className="p-3">
            <Calendar
              mode="range"
              selected={localRange}
              onSelect={handleCustomRange}
              numberOfMonths={2}
              defaultMonth={localRange.from ?? value.start}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
